import React from 'react';
import { T, NUMERIC } from './orders.theme';
import { COPY, money, formatPanelDateTime, formatRowDateTime } from './orders.copy';
import OrderStatusPill, { kindOf, PulsingDots } from './OrderStatusPill';
import {
  CheckIcon,
  ClockIcon,
  CrossIcon,
  PencilIcon,
  PersonIcon,
  PrinterIcon,
  ReceiptIcon,
} from './OrdersIcons';

const ITEM_COLUMNS = 'minmax(0,1fr) 34px 58px 62px';

/**
 * The open order: what is on it, what it came to, and the only place an order
 * can be edited, cancelled, or moved back to pending.
 *
 * The footer follows the order's state. A pending order leads with Pay and
 * names the amount going into the drawer; a settled one offers Mark pending
 * beside Cancel — the two consequential actions kept together, and kept out of
 * the list.
 */

const MetaRow = ({ label, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
    <span style={{ fontSize: 12.5, color: T.inkFaint, flexShrink: 0 }}>{label}</span>
    <span
      style={{
        fontSize: 12.5,
        color: T.ink,
        textAlign: 'right',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {children}
    </span>
  </div>
);

const AmountRow = ({ label, value, tone = 'default', strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
    <span
      style={{
        fontSize: strong ? 13.5 : 12.5,
        fontWeight: strong ? 700 : 400,
        color: strong ? T.ink : T.inkSoft,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: strong ? 19 : 12.5,
        fontWeight: strong || tone === 'danger' ? 700 : 400,
        letterSpacing: strong ? '-0.4px' : undefined,
        color: tone === 'danger' ? T.danger : T.ink,
        ...NUMERIC,
      }}
    >
      {value}
    </span>
  </div>
);

function EmptyPanel() {
  return (
    <aside style={panelShell}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 32,
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: T.radiusPill,
            background: T.canvas,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ReceiptIcon size={24} color={T.inkFaint} width={1.5} />
        </div>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
          {COPY.noSelectionTitle}
        </span>
        <span
          style={{
            fontSize: 12.5,
            color: T.inkSoft,
            textAlign: 'center',
            lineHeight: 1.5,
            maxWidth: 230,
          }}
        >
          {COPY.noSelectionBody}
        </span>
      </div>
    </aside>
  );
}

export default function OrderDetailPanel({
  entry,
  currency,
  busy,
  canCancel,
  canToggle,
  onClose,
  onPrint,
  onEdit,
  onCancel,
  onToggleStatus,
  onReprintTickets,
  onBillToCustomer,
}) {
  if (!entry) return <EmptyPanel />;

  const { order } = entry;
  const items = Array.isArray(order.items) ? order.items : [];

  /**
   * VAT is the order's own, not a box somebody ticks here.
   *
   * It used to be a checkbox at a hard-coded 5%, which meant the figure on
   * screen depended on who had clicked what — and matched the books only by
   * luck. The server now charges it, snapshots the rate on the order and
   * stores the amount, so this shows what was actually charged. An order with
   * no VAT shows no VAT line at all.
   */
  const vatAmount = entry.vatAmount;
  const grandTotal = entry.totalWithVat;
  const showBreakdown = entry.discount > 0 || vatAmount > 0;

  // An invoice settles on the customer's book, so it has no status action at
  // all; neither does a cancelled order.
  const settleable = canToggle && !entry.isInvoice && !entry.isCanceled;

  /**
   * Billing a customer settles a pending order, so only a pending order offers
   * it. One already on a book cannot be put on one twice, one already paid has
   * nothing left to owe, and a cancelled one is owed by nobody.
   */
  const billable =
    Boolean(onBillToCustomer) && entry.isPending && !entry.isInvoice && !entry.isCanceled;
  const showPay = settleable && entry.isPending;
  const showMarkPending = settleable && !entry.isPending;

  const orderTypeLabel = [
    order.orderType,
    order.tableNumber ? `Table ${order.tableNumber}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <aside style={panelShell}>
      {/* Header */}
      <div
        style={{
          padding: '15px 16px 13px',
          borderBottom: `1px solid ${T.hairline}`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.2px', color: T.ink }}>
            Dalab #{entry.orderNumber}
          </span>
          <OrderStatusPill kind={kindOf(entry)} />
        </div>
        <button type="button" onClick={onClose} aria-label="Close" style={closeButton}>
          <CrossIcon size={16} color={T.inkFaint} width={1.8} />
        </button>
      </div>

      {/* Meta */}
      <div
        style={{
          padding: '13px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 9,
          borderBottom: `1px solid ${T.hairline}`,
        }}
      >
        {entry.customerName && <MetaRow label={COPY.customer}>{entry.customerName}</MetaRow>}
        {/* Only a settled cash order has one, and only since the pay step
            started asking — an older one simply shows no row. */}
        {entry.paymentAccount ? (
          <MetaRow label={COPY.paidInto}>{entry.paymentAccount}</MetaRow>
        ) : null}
        <MetaRow label={COPY.date}>{formatRowDateTime(entry.time)}</MetaRow>
        {entry.staffName && <MetaRow label={COPY.servedBy}>{entry.staffName}</MetaRow>}
        {orderTypeLabel && <MetaRow label={COPY.orderType}>{orderTypeLabel}</MetaRow>}
      </div>

      {/* Items and totals */}
      <div style={{ flex: 1, minHeight: 0, padding: '12px 16px', overflowY: 'auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: ITEM_COLUMNS,
            gap: 8,
            paddingBottom: 7,
            borderBottom: `1px solid ${T.hairline}`,
          }}
        >
          {[COPY.item, COPY.qty, COPY.price, COPY.total].map((label, i) => (
            <span
              key={label}
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: T.inkFaint,
                textAlign: i === 0 ? 'left' : i === 1 ? 'center' : 'right',
              }}
            >
              {label}
            </span>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 2 }}>
          {items.map((item, index) => (
            <div
              key={`${item.menuItem || item.name}-${index}`}
              style={{ display: 'grid', gridTemplateColumns: ITEM_COLUMNS, gap: 8, padding: '7px 0' }}
            >
              <span
                title={item.name}
                style={{
                  fontSize: 12.5,
                  color: T.ink,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.name}
              </span>
              <span style={{ fontSize: 12.5, color: T.inkSoft, textAlign: 'center', ...NUMERIC }}>
                {item.quantity}
              </span>
              <span style={{ fontSize: 12.5, color: T.inkSoft, textAlign: 'right', ...NUMERIC }}>
                {money(item.price, currency)}
              </span>
              <span
                style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, textAlign: 'right', ...NUMERIC }}
              >
                {money(item.subtotal, currency)}
              </span>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 10,
            paddingTop: 11,
            borderTop: `1px solid ${T.hairline}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 7,
          }}
        >
          {/* Gross and discount only appear when there is something to show. */}
          {showBreakdown && (
            <AmountRow label={COPY.grossTotal} value={money(entry.grossTotal, currency)} />
          )}
          {entry.discount > 0 && (
            <AmountRow
              label={COPY.discount}
              value={`−${money(entry.discount, currency)}`}
              tone="danger"
            />
          )}

          {vatAmount > 0 && (
            <AmountRow
              label={COPY.vatLine(entry.vatRate)}
              value={money(vatAmount, currency)}
            />
          )}

          <div style={{ paddingTop: 8, borderTop: `1px solid ${T.hairline}` }}>
            <AmountRow label={COPY.grandTotal} value={money(grandTotal, currency)} strong />
          </div>
        </div>
      </div>

      {/* Footer — every action an order has, including the way back to pending */}
      <div
        style={{
          padding: '11px 16px',
          borderTop: `1px solid ${T.hairline}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 7,
        }}
      >
        {showPay && (
          <button
            type="button"
            onClick={() => onToggleStatus(entry)}
            disabled={busy}
            style={payButton}
          >
            {busy ? (
              <PulsingDots />
            ) : (
              <React.Fragment>
                <CheckIcon size={16} color={T.surface} width={2.4} />
                <span style={{ fontSize: 13, fontWeight: 700, color: T.surface }}>
                  {COPY.pay} · <span style={NUMERIC}>{money(entry.total, currency)}</span>
                </span>
              </React.Fragment>
            )}
          </button>
        )}

        <div style={{ display: 'flex', gap: 7 }}>
          <button type="button" onClick={() => onPrint(entry)} style={primaryButton}>
            <PrinterIcon size={16} color={T.surface} />
            <span style={{ fontSize: 13, fontWeight: 700, color: T.surface }}>
              {COPY.printReceipt}
            </span>
          </button>
          <button type="button" onClick={() => onEdit(entry)} style={secondaryButton}>
            <PencilIcon size={15} color={T.ink} />
            <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{COPY.edit}</span>
          </button>
        </div>

        {/* The kitchen's copy, again.
            The phone has no reprint at all, so a station printer that was
            switched off when the order went in loses its ticket for good and
            somebody writes it out by hand. This is the button that saves
            that. It prints marked REPRINT, so a cook who finds two identical
            tickets on the spike knows which is which. */}
        {/* {onReprintTickets && (
          <button
            type="button"
            onClick={() => onReprintTickets(entry)}
            style={{ ...secondaryButton, width: '100%' }}
          >
            <PrinterIcon size={15} color={T.ink} />
            <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
              {COPY.kitchenTicket}
            </span>
          </button>
        )} */}

        {/* Moving the bill onto a customer account.
            Full width and on its own row because it is neither a print action
            nor a destructive one, and because it is the thing a cashier
            reaches for when somebody says "put it on my account". */}
        {billable && (
          <button
            type="button"
            onClick={() => onBillToCustomer(entry)}
            style={{ ...secondaryButton, width: '100%' }}
          >
            <PersonIcon size={15} color={T.ink} />
            <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
              {COPY.billToCustomer}
            </span>
          </button>
        )}

        {/* Reversing a payment and cancelling share a row: both are the order's
            consequential actions, and neither belongs in the list. */}
        {(showMarkPending || canCancel) && (
          <div style={{ display: 'flex', gap: 7 }}>
            {showMarkPending && (
              <button
                type="button"
                onClick={() => onToggleStatus(entry)}
                disabled={busy}
                style={outlinedPendingButton}
              >
                {busy ? (
                  <PulsingDots color={T.pendingInk} />
                ) : (
                  <React.Fragment>
                    <ClockIcon size={15} color={T.pendingInk} width={2} />
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: T.pendingInk }}>
                      {COPY.markPending}
                    </span>
                  </React.Fragment>
                )}
              </button>
            )}
            {canCancel && (
              <button type="button" onClick={() => onCancel(entry)} style={outlinedDangerButton}>
                <CrossIcon size={15} color={T.danger} width={1.8} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.danger }}>
                  {COPY.cancelOrder}
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

const panelShell = {
  width: T.panelWidth,
  flexShrink: 0,
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: T.radiusPanel,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const closeButton = {
  width: 26,
  height: 26,
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
};

const payButton = {
  height: 40,
  borderRadius: T.radiusControl,
  background: T.paid,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  cursor: 'pointer',
  font: 'inherit',
};

const primaryButton = {
  flex: 1,
  height: 38,
  borderRadius: T.radiusControl,
  background: T.brand,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  cursor: 'pointer',
  font: 'inherit',
};

const secondaryButton = {
  width: 92,
  height: 38,
  borderRadius: T.radiusControl,
  background: T.surface,
  border: `1px solid ${T.borderStrong}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  font: 'inherit',
};

/**
 * Half-width outlined buttons. The two borders are weighted to match, so Mark
 * pending and Cancel read as peers rather than one shouting at the other.
 */
const outlinedButton = {
  flex: 1,
  height: 38,
  borderRadius: T.radiusControl,
  background: T.surface,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  font: 'inherit',
};

const outlinedPendingButton = { ...outlinedButton, border: `1px solid ${T.pendingBorder}` };
const outlinedDangerButton = { ...outlinedButton, border: `1px solid ${T.dangerBorderStrong}` };
